import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { userService } from '../../services/api';
import { USER_ROLES, CERTIFICATE_TYPES, CERTIFICATE_TYPE_LABELS } from '../../utils/constants';

/**
 * Modal per raccogliere i dati del certificato medico prima dell'upload
 * - Se userId è fornito: crea certificato per quell'utente specifico
 * - Se userId è null: mostra selettore utenti per scegliere l'utente
 */
const CertificateDataModal = ({ 
  show, 
  onHide, 
  onSubmit, 
  userId 
}) => {
  const [formData, setFormData] = useState({
    certificateNumber: '',
    issueDate: '',
    expiryDate: '',
    certificateType: CERTIFICATE_TYPES.NON_COMPETITIVE,
    notes: ''
  });
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Carica lista utenti se userId non è specificato
  const loadUsers = useCallback(async () => {
    if (!userId) {
      try {
        setLoadingUsers(true);
        const response = await userService.getUsers({ limit: 100 }); // Carica più utenti per la selezione
        if (response.success) {
          // Filtra solo membri
          const filteredUsers = response.data.items.filter(user => 
            user.roles.includes(USER_ROLES.MEMBER)
          );
          setUsers(filteredUsers);
        } else {
          setError('Errore nel caricamento degli utenti');
        }
      } catch (err) {
        setError('Errore di connessione al server');
        console.error('Error loading users:', err);
      } finally {
        setLoadingUsers(false);
      }
    }
  }, [userId]);

  // Funzione per ottenere la data di oggi in formato YYYY-MM-DD
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Funzione per ottenere la data di un anno dopo in formato YYYY-MM-DD
  const getOneYearLaterDate = (fromDate = null) => {
    const date = fromDate ? new Date(fromDate) : new Date();
    date.setFullYear(date.getFullYear() + 1);
    return date.toISOString().split('T')[0];
  };

  // Imposta le date di default quando il modal viene aperto
  useEffect(() => {
    if (show) {
      const todayDate = getTodayDate();
      const expiryDate = getOneYearLaterDate(todayDate);
      
      setFormData({
        certificateNumber: '',
        issueDate: todayDate,
        expiryDate: expiryDate,
        certificateType: CERTIFICATE_TYPES.NON_COMPETITIVE,
        notes: ''
      });
      setFile(null);
      setError('');
      setSelectedUserId('');
      
      // Carica utenti se necessario
      if (!userId) {
        loadUsers();
      }
    }
  }, [show, userId, loadUsers]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Auto-generate expiry date (1 year from issue date)
  const handleIssueDateChange = (e) => {
    const issueDate = e.target.value;
    setFormData(prev => ({
      ...prev,
      issueDate,
      expiryDate: issueDate ? getOneYearLaterDate(issueDate) : prev.expiryDate
    }));
  };

  // Gestione upload file opzionale
  const validateFile = (selectedFile) => {
    if (selectedFile.size > 10 * 1024 * 1024) {
      return 'Il file è troppo grande. Dimensione massima: 10MB';
    }

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(selectedFile.type)) {
      return 'Tipo di file non supportato. Usa PDF, JPG o PNG.';
    }

    return null;
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];

    if (!selectedFile) {
      setFile(null);
      return;
    }

    const validationError = validateFile(selectedFile);
    if (validationError) {
      setError(validationError);
      setFile(null);
      return;
    }

    setFile(selectedFile);
    setError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    // Validazione
    if (!formData.issueDate) {
      setError('La data di rilascio è obbligatoria');
      return;
    }
    if (!formData.expiryDate) {
      setError('La data di scadenza è obbligatoria');
      return;
    }
    
    // Se userId non è specificato, deve essere selezionato un utente
    if (!userId && !selectedUserId) {
      setError('Seleziona un utente per il certificato');
      return;
    }

    // Prepara i dati con userId corretto e file opzionale
    const submitData = {
      ...formData,
      userId: userId || selectedUserId,
      file: file // File opzionale
    };

    onSubmit(submitData);
    
    // Reset form
    setFormData({
      certificateNumber: '',
      issueDate: '',
      expiryDate: '',
      certificateType: CERTIFICATE_TYPES.NON_COMPETITIVE,
      notes: ''
    });
    setFile(null);
    setSelectedUserId('');
  };

  const handleClose = () => {
    setFormData({
      certificateNumber: '',
      issueDate: '',
      expiryDate: '',
      certificateType: CERTIFICATE_TYPES.NON_COMPETITIVE,
      notes: ''
    });
    setFile(null);
    setError('');
    setSelectedUserId('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Dati Certificato Medico</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Selettore utente solo quando userId non è specificato */}
          {!userId && (
            <Form.Group className="mb-4">
              <Form.Label>Utente *</Form.Label>
              <Form.Select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                required
              >
                <option value="">Seleziona un utente...</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.firstName} {user.lastName} - {user.email}
                  </option>
                ))}
              </Form.Select>
              {loadingUsers && (
                <Form.Text className="text-muted">Caricamento utenti...</Form.Text>
              )}
            </Form.Group>
          )}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Numero Certificato</Form.Label>
                <Form.Control
                  type="text"
                  name="certificateNumber"
                  value={formData.certificateNumber}
                  onChange={handleInputChange}
                  placeholder="CERT-2024-001"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Tipo Certificato</Form.Label>
                <Form.Select
                  name="certificateType"
                  value={formData.certificateType}
                  onChange={handleInputChange}
                >
                  {Object.entries(CERTIFICATE_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Data Rilascio *</Form.Label>
                <Form.Control
                  type="date"
                  name="issueDate"
                  value={formData.issueDate}
                  onChange={handleIssueDateChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Data Scadenza *</Form.Label>
                <Form.Control
                  type="date"
                  name="expiryDate"
                  value={formData.expiryDate}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Note Aggiuntive</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              placeholder="Note aggiuntive sul certificato..."
            />
          </Form.Group>

          {/* Sezione Upload File Opzionale */}
          <hr className="my-4" />
          <h6 className="mb-3">
            <i className="fas fa-file-upload me-2"></i>
            Allegato (Opzionale)
          </h6>
          
          <Form.Group className="mb-3">
            <Form.Label>Carica documento (PDF, JPG, PNG)</Form.Label>
            <Form.Control
              type="file"
              onChange={handleFileChange}
              accept=".pdf,.jpg,.jpeg,.png"
            />
            <Form.Text className="text-muted">
              Formati supportati: PDF, JPG, PNG. Dimensione massima: 10MB. 
              Il caricamento del file è opzionale.
            </Form.Text>
          </Form.Group>

          {file && (
            <div className="mb-3">
              <small className="text-success">
                <i className="fas fa-check-circle me-1"></i>
                File selezionato: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
              </small>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleClose}>
            Annulla
          </Button>
          <Button type="submit" variant="primary">
            <i className="fas fa-save me-1"></i>
            {file ? 'Crea Certificato con File' : 'Crea Certificato'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CertificateDataModal;